import React, { useRef, useCallback, useMemo } from "react";
import { List } from "../../Components/common/List/List";
import { ListProvider } from "../../Components/common/List/Providers/List";
import { useAppContext } from "../../AppProvider";
import { useListProps } from "./utilities/list";
import { Modal, ModalBody, UncontrolledTooltip } from "reactstrap";
import Delete from "./Delete";
import { createModalConfig, ModalConfig } from "../../Models/ModalConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faTrash,
  faSync,
} from "../../utilities/fontawesomeIcons";
import { useIndexUsersContext } from "./providers/Index";
import { DeleteUserProvider } from "./providers/Delete";
import { User } from "../../Models/User";
import Handler from "./Handler";
import ResetPassword from "./ResetPassword";
import { useTypes } from "../../utilities/types";
import { getBadge } from "../../Components/Badges/utilities";
import { HandlerUserProvider } from "./providers/Handler";
import { ResetPasswordUserProvider } from "./providers/ResetPassword";
import { getAllTimezones } from "../../utilities/utilities";

const AnyListProvider = ListProvider as any;
const AnyList = List as any;

export const Users: React.FC = () => {
  const listRef = useRef<{ refresh: () => void } | null>(null);
  const listProps = useListProps();
  const indexUsersContext = useIndexUsersContext();
  const modalConfig = indexUsersContext?.modalConfig;
  const setModalConfig = indexUsersContext?.setModalConfig;

  const renderItem = useCallback((item: User, index: number) => {
    return <ListItem listRef={listRef} item={item} index={index} />;
  }, []);

  const closeModal = () => {
    if (setModalConfig) {
      setModalConfig(createModalConfig());
    }
  };

  return (
    <>
      <AnyListProvider path={listProps.path} filters={listProps.filters}>
        <AnyList
          ref={listRef}
          title={listProps.title}
          create={listProps.create}
          banner={listProps.banner}
          massiveActions={listProps.massiveActions}
          renderItem={renderItem}
          pagination={listProps.pagination}
          selectRowsEnabled={listProps.selectRowsEnabled}
        />
      </AnyListProvider>

      <Modal isOpen={modalConfig?.isOpen} size={modalConfig?.size}>
        <ModalBody className={modalConfig?.className || ""}>
          {modalConfig?.action === "delete" && (
            <DeleteUserProvider
              data={modalConfig?.data}
              callback={() => {
                listRef.current?.refresh();
                closeModal();
              }}
            >
              <Delete />
            </DeleteUserProvider>
          )}
          {modalConfig?.action === "create" && (
            <HandlerUserProvider
              autosave={true}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
            >
              <Handler />
            </HandlerUserProvider>
          )}
          {modalConfig?.action == "edit" && (
            <HandlerUserProvider
              autosave={true}
              id={modalConfig?.data?.[0]}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
            >
              <Handler />
            </HandlerUserProvider>
          )}
          {modalConfig?.action == "resetPassword" && (
            <ResetPasswordUserProvider
              id={modalConfig?.data?.[0]}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
            >
              <ResetPassword />
            </ResetPasswordUserProvider>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default Users;

const ListItem: React.FC<{
  listRef: React.RefObject<{
    refresh: () => void;
  } | null>;
  item: User;
  index: number;
}> = React.memo(({ item, index }) => {
  const { dictionary, accessTokenPayload } = useAppContext();
  const indexUsersContext = useIndexUsersContext();
  const { booleanOptions } = useTypes();
  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data?: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = ""
    ) => {
      indexUsersContext?.setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [indexUsersContext]
  );

  const timezone = getAllTimezones().find(
    (o: any) => o.value === item.timeZone
  );

  const icons = useMemo(() => {
    return {
      enabled: item.enabled
        ? booleanOptions.find((o) => o.value === "TRUE")
        : booleanOptions.find((o) => o.value === "FALSE"),
    };
  }, [item]);

  return (
    <div className="list-item px-2">
      <div
        className="d-grid grid-column align-items-center"
        style={{
          gridTemplateColumns: "0.25fr 0.5fr 1fr 1fr 2fr 1fr auto",
        }}
      >
        <div>
          <label className="label-2">{dictionary.words.active}</label>
          <FontAwesomeIcon
            icon={icons?.enabled?.icon}
            title={icons.enabled?.label}
            color={icons.enabled?.color}
          />
        </div>
        <div>
          {item.owner && (
            <>
              <img id={"owner_" + index} src={item.owner.logoDarkUrl} />
              <UncontrolledTooltip placement="right" target={"owner_" + index}>
                <label className="label-2 text-light">
                  {dictionary.registries.company}:
                </label>
                <label className="d-block">{item.owner.name || "-"}</label>
                {item.owner.externalReference && (
                  <label className="fs-xs">
                    ({item.owner.externalReference})
                  </label>
                )}
              </UncontrolledTooltip>
            </>
          )}
        </div>
        <div>
          <label className="label-2">{dictionary.registries.name}</label>
          <label className="label-3">{item.name || "-"}</label>
        </div>
        <div>
          <label className="label-2">{dictionary.users.surname}</label>
          <label className="label-3">{item.surname || "-"}</label>
        </div>
        <div>
          <label className="label-2">{dictionary.registries.email}</label>
          <label className="label-3">{item.email || "-"}</label>
        </div>
        <div>
          <label className="label-2">{dictionary.locations.timezone}</label>
          {timezone ? (
            <label className="label-3">
              {getBadge({ text: timezone.label })}
            </label>
          ) : (
            "-"
          )}
        </div>

        <div className="text-end">
          <button
            title={dictionary.actions.edit}
            className="button-1 button-1-light button-1-icon me-1"
            onClick={() => {
              openModal(
                "edit",
                [item.id],
                "md",
                "",
                `${
                  dictionary.actions.edit
                } ${dictionary.users.user.toLowerCase()}`
              );
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          <button
            title={`${
              dictionary.actions.reset
            } ${dictionary.users.password.toLowerCase()}`}
            className="button-1 button-1-light button-1-icon me-1"
            onClick={() => {
              openModal(
                "resetPassword",
                [item.id],
                "md",
                "",
                `${
                  dictionary.actions.reset
                } ${dictionary.users.password.toLowerCase()}`
              );
            }}
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button
            title={dictionary.actions.delete}
            className="button-1 button-1-danger button-1-icon"
            onClick={() => {
              openModal(
                "delete",
                [item.id],
                "sm",
                "",
                dictionary.actions.delete
              );
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
    </div>
  );
});
